import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAuth } from '@/lib/AuthContext';

function useIsAuthed() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

// User
export function useMe() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/me'),
    enabled: authed,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch('/me', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// Dashboard
export function useDashboard() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard'),
    enabled: authed,
    refetchInterval: 30000,
  });
}

// Task completion
export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...body }) => api.post(`/tasks/${taskId}/complete`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

// Upload
export function useUpload() {
  return useMutation({
    mutationFn: (file) => api.upload('/upload', file),
  });
}

// Leaderboard
export function useLeaderboard(campaignId = null, limit = 50, offset = 0) {
  const authed = useIsAuthed();
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (campaignId) params.set('campaign', String(campaignId));

  return useQuery({
    queryKey: ['leaderboard', campaignId, limit, offset],
    queryFn: () => api.get(`/leaderboard?${params}`),
    enabled: authed,
  });
}

// Referral
export function useReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/referral', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard'] }),
  });
}

// Checkout
export function useCheckout() {
  return useMutation({
    mutationFn: () => api.post('/checkout', {}),
  });
}

// Admin
/**
 * Polled pending-reviews counter for the nav badge. 60s refetch keeps the
 * count fresh without hammering the DB; the endpoint is a single indexed
 * COUNT so cost is negligible. Caller must pass `enabled` so non-admin
 * users never poll the admin endpoint at all (gate on isAdmin/canReview).
 */
export function usePendingReviewsCount({ enabled = false } = {}) {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'pending-reviews-count'],
    queryFn: () => api.get('/admin/pending-reviews-count'),
    enabled: authed && enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // 403 for non-reviewer users — fail silently so non-admin nav doesn't error.
    retry: false,
  });
}

export function useAdminStats() {
  const authed = useIsAuthed();
  return useQuery({ queryKey: ['admin', 'stats'], queryFn: () => api.get('/admin/stats'), enabled: authed });
}

export function useAdminUsers(search = '', limit = 50, offset = 0) {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'users', search, limit, offset],
    queryFn: () => api.get(`/admin/users?search=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`),
    enabled: authed,
  });
}

export function useAdminUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }) => api.patch(`/admin/users/${userId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAdminReviews(filters = {}) {
  const authed = useIsAuthed();
  const {
    status = 'pending_review',
    taskType = '',
    search = '',
    fromDate = '',
    toDate = '',
    limit = 50,
    offset = 0,
  } = filters;

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (taskType) params.set('task_type', taskType);
  if (search) params.set('search', search);
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  return useQuery({
    queryKey: ['admin', 'reviews', status, taskType, search, fromDate, toDate, limit, offset],
    queryFn: () => api.get(`/admin/reviews?${params}`),
    enabled: authed,
  });
}

export function useAdminReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ completionId, action, reasonCode, reasonNote }) =>
      api.post(`/admin/reviews/${completionId}`, {
        action,
        reason_code: reasonCode,
        reason_note: reasonNote,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pending-reviews-count'] });
      qc.invalidateQueries({ queryKey: ['admin', 'rejection-report'] });
    },
  });
}

export function useRejectionReport(filters = {}) {
  const authed = useIsAuthed();
  const params = new URLSearchParams();
  if (filters.fromDate) params.set('from_date', filters.fromDate);
  if (filters.toDate) params.set('to_date', filters.toDate);
  if (filters.campaignId) params.set('campaign_id', String(filters.campaignId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['admin', 'rejection-report', filters],
    queryFn: () => api.get(`/admin/rejection-report${qs ? `?${qs}` : ''}`),
    enabled: authed,
  });
}

export function useAdminSettings() {
  const authed = useIsAuthed();
  return useQuery({ queryKey: ['admin', 'settings'], queryFn: () => api.get('/admin/settings'), enabled: authed });
}

export function useAdminUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch('/admin/settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });
}

// Notifications (poll every 30s)
export function useNotifications() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications'),
    enabled: authed,
    refetchInterval: 30000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/notifications/read', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// Admin tasks
export function useAdminTasks() {
  const authed = useIsAuthed();
  return useQuery({ queryKey: ['admin', 'tasks'], queryFn: () => api.get('/admin/tasks'), enabled: authed });
}

export function useAdminUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...body }) => api.patch(`/admin/tasks/${taskId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// Campaigns (admin)
export function useAdminCampaigns(filters = {}) {
  const authed = useIsAuthed();
  const { status = '', search = '', isTemplate = null, limit = 50, offset = 0 } = filters;

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  if (isTemplate !== null && isTemplate !== '') params.set('is_template', isTemplate ? '1' : '0');
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  return useQuery({
    queryKey: ['admin', 'campaigns', status, search, isTemplate, limit, offset],
    queryFn: () => api.get(`/admin/campaigns?${params}`),
    enabled: authed,
  });
}

export function useAdminCampaign(id) {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'campaigns', id],
    queryFn: () => api.get(`/admin/campaigns/${id}`),
    enabled: authed && !!id,
  });
}

export function useCampaignAnalytics(id) {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'campaigns', id, 'analytics'],
    queryFn: () => api.get(`/admin/campaigns/${id}/analytics`),
    enabled: authed && !!id,
    // Aggregations are expensive; cache 30s so tab-switching doesn't refetch.
    staleTime: 30_000,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/admin/campaigns', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'campaigns'] }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, ...body }) => api.patch(`/admin/campaigns/${campaignId}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['admin', 'campaigns', vars.campaignId] });
    },
  });
}

export function useUpdateCampaignTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, tasks }) => api.patch(`/admin/campaigns/${campaignId}/tasks`, { tasks }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'campaigns', vars.campaignId] });
    },
  });
}

export function useActivateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId) => api.post(`/admin/campaigns/${campaignId}/activate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'campaigns'] }),
  });
}

export function useArchiveCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId) => api.post(`/admin/campaigns/${campaignId}/archive`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'campaigns'] }),
  });
}

// Public campaign
export function useActiveCampaign() {
  return useQuery({
    queryKey: ['campaign', 'active'],
    queryFn: () => api.get('/campaign/active'),
  });
}

// Campaign image upload
export function useUploadCampaignImage() {
  return useMutation({
    mutationFn: ({ campaignId, file }) => api.upload(`/admin/campaigns/${campaignId}/images`, file),
  });
}

// Landing page sections — public (visible-only, ordered)
export function useLandingSections() {
  return useQuery({
    queryKey: ['landing', 'sections'],
    queryFn: () => api.get('/landing/sections'),
    staleTime: 60_000,
  });
}

// Landing page sections — admin (full list with visibility flag for management UI)
export function useAdminLandingSections() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'landing', 'sections'],
    queryFn: () => api.get('/admin/landing/sections'),
    enabled: authed,
  });
}

export function useUpdateLandingSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sections) => api.patch('/admin/landing/sections', { sections }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'landing', 'sections'] });
      qc.invalidateQueries({ queryKey: ['landing', 'sections'] });
    },
  });
}

// Lightweight landing-page health stats. GA4/Meta remain source of truth for
// per-source attribution — this hook is just the at-a-glance summary the admin
// panel renders next to the section management UI.
export function useLandingAnalytics() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'landing', 'analytics'],
    queryFn: () => api.get('/admin/landing/analytics'),
    enabled: authed,
    staleTime: 60_000,
  });
}

// Survey
export function useSurvey() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['survey'],
    queryFn: () => api.get('/survey'),
    enabled: authed,
  });
}

export function useAdminSurveyResponses(limit = 50, offset = 0) {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['admin', 'survey', limit, offset],
    queryFn: () => api.get(`/admin/survey-responses?limit=${limit}&offset=${offset}`),
    enabled: authed,
  });
}

export function useSubmitSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/survey', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['survey'] });
    },
  });
}

// Social accounts
export function useSocialAccounts() {
  const authed = useIsAuthed();
  return useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => api.get('/social-accounts'),
    enabled: authed,
  });
}

export function useLinkSocialAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/social-accounts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-accounts'] }),
  });
}

export function useUnlinkSocialAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider) => api.delete(`/social-accounts/${provider}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-accounts'] }),
  });
}

// Social linking — initiate OAuth popup flow
export function useInitiateSocialLink() {
  const qc = useQueryClient();

  const initiate = async (provider) => {
    // 1. Get the Auth0 authorize URL from our backend
    const data = await api.get(`/social-accounts/connect/${provider}`);
    const url = data.url;
    if (!url) throw new Error('No authorize URL returned');

    // 2. Open popup
    const width = 500;
    const height = 650;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      url,
      'social-link-popup',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup) {
      throw new Error('Popup was blocked by the browser');
    }

    // 3. Listen for result via postMessage
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        clearInterval(pollTimer);
      };

      const onMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'social-link-result') return;
        cleanup();
        if (event.data.success) {
          qc.invalidateQueries({ queryKey: ['social-accounts'] });
          resolve(event.data);
        } else {
          reject(new Error(event.data.error || 'Linking failed'));
        }
      };

      // Poll in case the popup closes without posting a message (user closed it)
      const pollTimer = setInterval(() => {
        if (popup && popup.closed) {
          cleanup();
          reject(new Error('cancelled'));
        }
      }, 500);

      window.addEventListener('message', onMessage);
    });
  };

  return { initiate };
}
